import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Icon from './Icon.jsx'

/**
 * A right-click menu: full-screen invisible backdrop (click-away and native-right-click-away
 * both dismiss it) plus a panel that measures itself and clamps into the viewport, since the
 * click point is rarely where the menu actually has room to render. Escape closes it too.
 */
export default function ContextMenu({ x, y, onClose, children }) {
  const ref = useRef(null)
  const [pos, setPos] = useState({ top: y, left: x })

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const { offsetWidth: w, offsetHeight: h } = el
    setPos({
      top: Math.max(4, Math.min(y, window.innerHeight - h - 8)),
      left: Math.max(4, Math.min(x, window.innerWidth - w - 8)),
    })
  }, [x, y])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="ctx-backdrop"
      onClick={onClose}
      onContextMenu={(e) => {
        e.preventDefault()
        onClose()
      }}
    >
      <div ref={ref} className="ctx-menu" style={{ top: pos.top, left: pos.left }} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

export function ContextMenuLabel({ children }) {
  return <div className="ctx-label">{children}</div>
}

export function ContextMenuSeparator() {
  return <div className="ctx-sep" role="separator" />
}

/**
 * `icon` marks an action item; `checked` marks a selectable one (e.g. an appearance
 * picker) — a menu uses one style or the other, not both, so a caller only ever passes one.
 */
export function ContextMenuItem({ icon, checked, children, onClick, disabled }) {
  return (
    <button className="ctx-item" disabled={disabled} onClick={onClick}>
      {icon && (
        <span className="ctx-item-icon">
          <Icon name={icon} size={14} />
        </span>
      )}
      {checked !== undefined && (
        <span className="ctx-item-check">
          {checked && <Icon name="check" size={13} strokeWidth={2.4} />}
        </span>
      )}
      <span>{children}</span>
    </button>
  )
}
